import type { OrchestrationThreadActivity, ThreadTokenUsageSnapshot } from "@amoreg/contracts";

export const DEFAULT_CONTEXT_ESTIMATE_CHARS_PER_TOKEN = 4;

export type ContextUsageSource = "provider" | "estimate";

type NullableContextWindowUsage = {
  readonly [Key in keyof ThreadTokenUsageSnapshot]: undefined extends ThreadTokenUsageSnapshot[Key]
    ? Exclude<ThreadTokenUsageSnapshot[Key], undefined> | null
    : ThreadTokenUsageSnapshot[Key];
};

export type ContextWindowSnapshot = NullableContextWindowUsage & {
  readonly remainingTokens: number | null;
  readonly usedPercentage: number | null;
  readonly remainingPercentage: number | null;
  readonly updatedAt: string;
};

export interface ProviderContextUsageInput {
  readonly inputTokens?: number;
  readonly cachedInputTokens?: number;
  readonly cacheCreationInputTokens?: number;
  readonly cachedInputTokensIncludedInInput?: boolean;
  readonly outputTokens?: number;
  readonly reasoningOutputTokens?: number;
  readonly totalProcessedTokens?: number;
  readonly maxTokens?: number;
  readonly lastUsedTokens?: number;
  readonly compactsAutomatically?: boolean;
  readonly toolUses?: number;
  readonly durationMs?: number;
}

export interface EstimatedContextUsageInput {
  readonly maxTokens?: number;
  readonly compactsAutomatically?: boolean;
  readonly charsPerToken?: number;
  readonly categories: {
    readonly system?: string | number;
    readonly harness?: string | number;
    readonly tools?: string | number;
    readonly skills?: string | number;
    readonly workspace?: string | number;
    readonly organization?: string | number;
    readonly attachmentsText?: string | number;
    readonly attachmentsImages?: number;
    readonly toolInputs?: string | number;
    readonly toolResults?: string | number;
    readonly subagents?: string | number;
    readonly previousTurns?: string | number;
    readonly cachedPrefix?: string | number;
    readonly currentUser?: string | number;
    readonly assistantOutput?: string | number;
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : null;
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function asSource(value: unknown): ContextUsageSource | null {
  return value === "provider" || value === "estimate" ? value : null;
}

function count(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= 0
    ? Math.round(value)
    : undefined;
}

function positiveCount(value: unknown): number | undefined {
  const rounded = count(value);
  return rounded !== undefined && rounded > 0 ? rounded : undefined;
}

function addOptional(
  target: Record<string, unknown>,
  key: string,
  value: number | boolean | string | Record<string, number> | undefined,
): void {
  if (value !== undefined) {
    target[key] = value;
  }
}

export function contextTokensFromProviderUsage(input: ProviderContextUsageInput): number {
  // An input total that already counts cached tokens counts cache writes too
  // (AI SDK / Gateway usage: eve reported inputTokens 688,260 with
  // cacheWriteTokens 688,257, 3 of them uncached). Adding either again showed
  // a 688k context as 1.4M of 1M.
  if (input.cachedInputTokensIncludedInInput) {
    return count(input.inputTokens) ?? 0;
  }
  return (
    (count(input.inputTokens) ?? 0) +
    (count(input.cachedInputTokens) ?? 0) +
    (count(input.cacheCreationInputTokens) ?? 0)
  );
}

export function buildProviderContextUsageSnapshot(
  input: ProviderContextUsageInput,
): ThreadTokenUsageSnapshot | undefined {
  const usedTokens = contextTokensFromProviderUsage(input);
  if (usedTokens <= 0) {
    return undefined;
  }
  const snapshot: ThreadTokenUsageSnapshot = {
    usedTokens,
    source: "provider",
    lastUsedTokens: count(input.lastUsedTokens) ?? usedTokens,
  };
  addOptional(snapshot, "totalProcessedTokens", positiveCount(input.totalProcessedTokens));
  addOptional(snapshot, "maxTokens", positiveCount(input.maxTokens));
  addOptional(snapshot, "inputTokens", count(input.inputTokens));
  addOptional(snapshot, "cachedInputTokens", count(input.cachedInputTokens));
  addOptional(snapshot, "cacheCreationInputTokens", count(input.cacheCreationInputTokens));
  addOptional(snapshot, "outputTokens", count(input.outputTokens));
  addOptional(snapshot, "reasoningOutputTokens", count(input.reasoningOutputTokens));
  addOptional(snapshot, "lastInputTokens", count(input.inputTokens));
  addOptional(snapshot, "lastCachedInputTokens", count(input.cachedInputTokens));
  addOptional(snapshot, "lastCacheCreationInputTokens", count(input.cacheCreationInputTokens));
  addOptional(snapshot, "lastOutputTokens", count(input.outputTokens));
  addOptional(snapshot, "lastReasoningOutputTokens", count(input.reasoningOutputTokens));
  addOptional(snapshot, "toolUses", count(input.toolUses));
  addOptional(snapshot, "durationMs", count(input.durationMs));
  addOptional(snapshot, "compactsAutomatically", input.compactsAutomatically);
  return snapshot;
}

function estimateCategoryTokens(value: string | number | undefined, charsPerToken: number): number {
  if (typeof value === "number") {
    const rounded = count(value);
    return rounded !== undefined ? Math.ceil(rounded / charsPerToken) : 0;
  }
  if (typeof value === "string" && value.length > 0) {
    return Math.ceil(value.length / charsPerToken);
  }
  return 0;
}

export function buildEstimatedContextUsageSnapshot(
  input: EstimatedContextUsageInput,
): ThreadTokenUsageSnapshot | undefined {
  const charsPerToken =
    typeof input.charsPerToken === "number" &&
    Number.isFinite(input.charsPerToken) &&
    input.charsPerToken > 0
      ? input.charsPerToken
      : DEFAULT_CONTEXT_ESTIMATE_CHARS_PER_TOKEN;
  const breakdown: Record<string, number> = {};
  for (const [key, value] of Object.entries(input.categories)) {
    const tokens =
      key === "attachmentsImages" && typeof value === "number"
        ? (count(value) ?? 0)
        : estimateCategoryTokens(value, charsPerToken);
    if (tokens > 0) {
      breakdown[key] = tokens;
    }
  }
  const usedTokens = Object.values(breakdown).reduce((sum, value) => sum + value, 0);
  if (usedTokens <= 0) {
    return undefined;
  }
  const maxTokens = positiveCount(input.maxTokens);
  const snapshot: ThreadTokenUsageSnapshot = {
    usedTokens,
    source: "estimate",
    lastUsedTokens: usedTokens,
    breakdown,
  };
  addOptional(snapshot, "maxTokens", maxTokens);
  addOptional(snapshot, "compactsAutomatically", input.compactsAutomatically);
  return snapshot;
}

export function isResolvableContextWindowPayload(payload: unknown): boolean {
  const record = asRecord(payload);
  const usedTokens = asFiniteNumber(record?.usedTokens);
  return usedTokens !== null && usedTokens >= 0;
}

export function deriveLatestContextWindowSnapshot(
  activities: ReadonlyArray<OrchestrationThreadActivity>,
): ContextWindowSnapshot | null {
  for (let index = activities.length - 1; index >= 0; index -= 1) {
    const activity = activities[index];
    if (!activity || activity.kind !== "context-window.updated") {
      continue;
    }

    const payload = asRecord(activity.payload);
    const usedTokens = asFiniteNumber(payload?.usedTokens);
    if (usedTokens === null || usedTokens < 0) {
      continue;
    }

    const maxTokens = asFiniteNumber(payload?.maxTokens);
    const usedPercentage =
      maxTokens !== null && maxTokens > 0 ? Math.min(100, (usedTokens / maxTokens) * 100) : null;
    const remainingTokens =
      maxTokens !== null ? Math.max(0, Math.round(maxTokens - usedTokens)) : null;
    const remainingPercentage = usedPercentage !== null ? Math.max(0, 100 - usedPercentage) : null;

    return {
      usedTokens,
      source: asSource(payload?.source),
      totalProcessedTokens: asFiniteNumber(payload?.totalProcessedTokens),
      maxTokens,
      remainingTokens,
      usedPercentage,
      remainingPercentage,
      inputTokens: asFiniteNumber(payload?.inputTokens),
      cachedInputTokens: asFiniteNumber(payload?.cachedInputTokens),
      cacheCreationInputTokens: asFiniteNumber(payload?.cacheCreationInputTokens),
      outputTokens: asFiniteNumber(payload?.outputTokens),
      reasoningOutputTokens: asFiniteNumber(payload?.reasoningOutputTokens),
      lastUsedTokens: asFiniteNumber(payload?.lastUsedTokens),
      lastInputTokens: asFiniteNumber(payload?.lastInputTokens),
      lastCachedInputTokens: asFiniteNumber(payload?.lastCachedInputTokens),
      lastCacheCreationInputTokens: asFiniteNumber(payload?.lastCacheCreationInputTokens),
      lastOutputTokens: asFiniteNumber(payload?.lastOutputTokens),
      lastReasoningOutputTokens: asFiniteNumber(payload?.lastReasoningOutputTokens),
      breakdown: asRecord(payload?.breakdown) as Record<string, number> | null,
      toolUses: asFiniteNumber(payload?.toolUses),
      durationMs: asFiniteNumber(payload?.durationMs),
      compactsAutomatically: asBoolean(payload?.compactsAutomatically) ?? false,
      updatedAt: activity.createdAt,
    };
  }

  return null;
}

export function formatContextWindowTokens(value: number | null): string {
  if (value === null || !Number.isFinite(value)) {
    return "0";
  }
  if (value < 1_000) {
    return `${Math.round(value)}`;
  }
  if (value < 10_000) {
    return `${(value / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  if (value < 1_000_000) {
    return `${Math.round(value / 1_000)}k`;
  }
  return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
}

/**
 * A token count to three significant figures with a unit suffix (`804K`,
 * `1.05M`) — the form the compaction divider and the usage page share.
 */
export function formatTokenCount(value: number): string {
  const abs = Math.abs(value);
  if (Math.round(abs) < 1e3) return String(Math.round(value));
  // 999.5–999.99 has no unit of its own yet rounds to 1,000: it reads in K.
  const found = TOKEN_UNITS.findIndex(([scale]) => abs >= scale);
  const index = found === -1 ? TOKEN_UNITS.length - 1 : found;
  const [scale, suffix] = TOKEN_UNITS[index]!;
  const scaled = trimTokenUnit(value / scale);
  // The unit is settled after rounding, so 999,600 reads "1M", not "1000K".
  const larger = TOKEN_UNITS[index - 1];
  if (larger !== undefined && Math.abs(Number(scaled)) >= 1000) {
    return `${trimTokenUnit(value / larger[0])}${larger[1]}`;
  }
  return `${scaled}${suffix}`;
}

const TOKEN_UNITS = [
  [1e12, "T"],
  [1e9, "B"],
  [1e6, "M"],
  [1e3, "K"],
] as const;

function trimTokenUnit(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toFixed(digits).replace(/\.0+$/, "");
}

/**
 * The context-compaction divider's label. Both counts: "Compacted context
 * 779K → 120K tokens"; only the size compaction started from (the smaller
 * context is measured on the next model call): "Compacted context from 779K
 * tokens"; neither: "Context compacted".
 */
export function formatContextCompactionSummary(input: {
  readonly beforeTokens?: number | undefined;
  readonly afterTokens?: number | undefined;
}): string {
  const { beforeTokens, afterTokens } = input;
  if (beforeTokens !== undefined && afterTokens !== undefined) {
    return `Compacted context ${formatTokenCount(beforeTokens)} → ${formatTokenCount(afterTokens)} tokens`;
  }
  if (beforeTokens !== undefined) {
    return `Compacted context from ${formatTokenCount(beforeTokens)} tokens`;
  }
  return "Context compacted";
}
